import api, { apiWithRetry } from './api';
import { checkContentLocale, checkContentArrayLocale } from './localeHelper';

/**
 * Article service for fetching articles from Strapi API
 */

/**
 * Build query parameters for Strapi API
 * @param {Object} options - Query options
 * @returns {Object} Query parameters
 */
const buildArticleParams = (options = {}) => {
  const {
    page = 1,
    pageSize = 10,
    sort = 'publishedAt:desc',
    category = null,
    featured = null,
    populate = '*',
    locale = null,
  } = options;

  const params = {
    'pagination[page]': page,
    'pagination[pageSize]': pageSize,
    'sort': sort,
    'populate': populate,
  };

  // Add locale if specified
  if (locale) {
    params['locale'] = locale;
  }

  // Add category filter if specified
  if (category) {
    params['filters[categories][slug][$eq]'] = category;
  }

  // Add featured filter if specified
  if (featured !== null) {
    params['filters[isFeatured][$eq]'] = featured;
  }

  return params;
};

export const articleService = {
  /**
   * Get all articles with pagination and filtering
   * @param {Object} options - Query options
   * @param {number} options.page - Page number (default: 1)
   * @param {number} options.pageSize - Items per page (default: 10)
   * @param {string} options.sort - Sort order (default: 'publishedAt:desc')
   * @param {string} options.category - Filter by category slug
   * @param {boolean} options.featured - Filter by featured status
   * @param {string} options.populate - Relations to populate (default: '*')
   * @returns {Promise<Object>} Articles data with pagination meta
   */
  getArticles: async (options = {}) => {
    try {
      const params = buildArticleParams(options);
      
      const response = await apiWithRetry(() => 
        api.get('/api/articles', { params })
      );
      
      // Check locale for all articles
      const data = response.data;
      if (data.data) {
        data.data = checkContentArrayLocale(data.data);
      }
      
      return data;
    } catch (error) {
      console.error('Error fetching articles:', error);
      throw new Error(error.userMessage || 'Failed to fetch articles');
    }
  },

  /**
   * Get featured articles
   * @param {number} limit - Maximum number of articles to return (default: 1)
   * @param {string} locale - Optional locale override
   * @returns {Promise<Object>} Featured articles data
   */
  getFeaturedArticles: async (limit = 1, locale = null) => {
    try {
      const params = {
        'filters[isFeatured][$eq]': true,
        'sort': 'publishedAt:desc',
        'pagination[pageSize]': limit,
        'populate': '*',
      };
      
      if (locale) {
        params['locale'] = locale;
      }
      
      const response = await apiWithRetry(() => 
        api.get('/api/articles', { params })
      );
      
      return response.data;
    } catch (error) {
      console.error('Error fetching featured articles:', error);
      throw new Error(error.userMessage || 'Failed to fetch featured articles');
    }
  },

  /**
   * Get recent articles
   * @param {number} limit - Maximum number of articles to return (default: 9)
   * @param {string} locale - Optional locale override
   * @returns {Promise<Object>} Recent articles data
   */
  getRecentArticles: async (limit = 9, locale = null) => {
    try {
      const params = {
        'sort': 'publishedAt:desc',
        'pagination[pageSize]': limit,
        'populate': '*',
      };
      
      if (locale) {
        params['locale'] = locale;
      }
      
      const response = await apiWithRetry(() => 
        api.get('/api/articles', { params })
      );
      
      return response.data;
    } catch (error) {
      console.error('Error fetching recent articles:', error);
      throw new Error(error.userMessage || 'Failed to fetch recent articles');
    }
  },

  /**
   * Get single article by ID
   * @param {number} id - Article ID
   * @param {string} populate - Relations to populate (default: '*')
   * @returns {Promise<Object>} Article data
   */
  getArticleById: async (id, populate = '*') => {
    try {
      const params = { populate };
      
      const response = await apiWithRetry(() => 
        api.get(`/api/articles/${id}`, { params })
      );
      
      return response.data;
    } catch (error) {
      console.error(`Error fetching article ${id}:`, error);
      throw new Error(error.userMessage || 'Failed to fetch article');
    }
  },

  /**
   * Get article by slug
   * @param {string} slug - Article slug
   * @param {string} populate - Relations to populate (default: '*')
   * @returns {Promise<Object>} Article data
   */
  getArticleBySlug: async (slug, populate = '*', locale = null) => {
    try {
      // Detect locale from slug suffix if not explicitly provided
      const localeMatch = slug.match(/-(vi|pl|en)$/);
      const detectedLocale = localeMatch ? localeMatch[1] : null;
      const effectiveLocale = locale || detectedLocale;
      
      const params = {
        'filters[slug][$eq]': slug,
        'populate': populate,
      };
      
      // If we have a locale, use it explicitly
      if (effectiveLocale) {
        params.locale = effectiveLocale;
      }
      
      const response = await apiWithRetry(() => 
        api.get('/api/articles', { params })
      );
      
      // Return first article if found
      if (response.data.data && response.data.data.length > 0) {
        return checkContentLocale(response.data.data[0]);
      }
      
      // If not found and slug has locale suffix, try the base slug in that locale
      const slugWithoutLocale = slug.replace(/-(en|vi|pl)$/, '');
      if (slugWithoutLocale !== slug) {
        const fallbackParams = {
          'filters[slug][$eq]': slugWithoutLocale,
          'populate': populate,
          'locale': detectedLocale || 'en',
        };
        
        const fallbackResponse = await apiWithRetry(() => 
          api.get('/api/articles', { params: fallbackParams })
        );
        
        if (fallbackResponse.data.data && fallbackResponse.data.data.length > 0) {
          return checkContentLocale(fallbackResponse.data.data[0]);
        }
      }
      
      // Last resort: try all locales
      for (const tryLocale of ['en', 'vi', 'pl']) {
        if (tryLocale === effectiveLocale) continue;
        const lastParams = {
          'filters[slug][$eq]': slug,
          'populate': populate,
          'locale': tryLocale,
        };
        const lastResponse = await apiWithRetry(() => 
          api.get('/api/articles', { params: lastParams })
        );
        if (lastResponse.data.data && lastResponse.data.data.length > 0) {
          return checkContentLocale(lastResponse.data.data[0]);
        }
      }
      
      throw new Error('Article not found');
    } catch (error) {
      console.error(`Error fetching article by slug ${slug}:`, error);
      throw new Error(error.userMessage || 'Failed to fetch article');
    }
  },

  /**
   * Get articles by category
   * @param {string} categorySlug - Category slug
   * @param {Object} options - Additional query options
   * @returns {Promise<Object>} Articles data
   */
  getArticlesByCategory: async (categorySlug, options = {}) => {
    try {
      const params = buildArticleParams({
        ...options,
        category: categorySlug,
      });
      
      const response = await apiWithRetry(() => 
        api.get('/api/articles', { params })
      );
      
      return response.data;
    } catch (error) {
      console.error(`Error fetching articles by category ${categorySlug}:`, error);
      throw new Error(error.userMessage || 'Failed to fetch articles by category');
    }
  },

  /**
   * Get related articles (same categories)
   * @param {string} categorySlug - Category slug
   * @param {number} excludeId - Article ID to exclude from results
   * @param {number} limit - Maximum number of articles to return (default: 3)
   * @returns {Promise<Object>} Related articles data
   */
  getRelatedArticles: async (categorySlug, excludeId, limit = 3) => {
    try {
      const params = {
        'filters[categories][slug][$eq]': categorySlug,
        'filters[id][$ne]': excludeId,
        'sort': 'publishedAt:desc',
        'pagination[pageSize]': limit,
        'populate': '*',
      };
      
      const response = await apiWithRetry(() => 
        api.get('/api/articles', { params })
      );
      
      return response.data;
    } catch (error) {
      console.error('Error fetching related articles:', error);
      throw new Error(error.userMessage || 'Failed to fetch related articles');
    }
  },

  /**
   * Search articles by query string
   * @param {string} query - Search query
   * @param {Object} options - Additional query options
   * @returns {Promise<Object>} Search results
   */
  searchArticles: async (query, options = {}) => {
    try {
      if (!query || query.trim().length === 0) {
        return { data: [], meta: { pagination: { total: 0 } } };
      }

      const {
        page = 1,
        pageSize = 10,
        sort = 'publishedAt:desc',
      } = options;

      const params = {
        'filters[$or][0][title][$containsi]': query,
        'filters[$or][1][body][$containsi]': query,
        'filters[$or][2][author][$containsi]': query,
        'pagination[page]': page,
        'pagination[pageSize]': pageSize,
        'sort': sort,
        'populate': '*',
      };
      
      const response = await apiWithRetry(() => 
        api.get('/api/articles', { params })
      );
      
      return response.data;
    } catch (error) {
      console.error('Error searching articles:', error);
      throw new Error(error.userMessage || 'Failed to search articles');
    }
  },
};

export default articleService;
